import { Injectable, inject } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import {
  Action,
  createSelector,
  NgxsAfterBootstrap,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import { catchError, finalize, Observable, of, tap } from 'rxjs';
import { ActivityModel } from '@wilson/activities/models';
import { NestedKeyOf } from '@wilson/utils';
import {
  ActivityPropertySetting,
  ActivityPropertySettingsGateway,
  AppActivityPropertiesAvailability,
} from '../activity-property-settings.gateway';
import { PropertyAvailability } from '@wilson/config';
import { InitializeActivityPropertySettingsState } from './activity-property-settings.action';

export interface ActivityPropertySettingsStateModel {
  settings: ActivityPropertySetting[];
  isLoading: boolean;
  /**
   * Flips to `true` after the first load attempt completes — whether that was
   * a successful Storage rehydration, a successful gateway fetch, or a failed
   * gateway fetch (offline / network error).  Once `true`, `mobileAvailability`
   * resolves to a real `Map` (possibly empty, meaning "all fields available")
   * instead of `undefined`, so the form is never stuck behind the loading guard
   * when the device is offline and no cached settings exist yet.
   */
  isLoaded: boolean;
}

const STATE_KEY = 'activityPropertySettings';

const defaults: ActivityPropertySettingsStateModel = {
  settings: [],
  isLoading: false,
  isLoaded: false,
};

@State<ActivityPropertySettingsStateModel>({
  name: STATE_KEY,
  defaults,
})
@Injectable()
export class ActivityPropertySettingsState implements NgxsAfterBootstrap {
  private readonly gateway = inject(ActivityPropertySettingsGateway);
  private readonly storage = inject(Storage);

  @Selector()
  static settings(
    state: ActivityPropertySettingsStateModel,
  ): ActivityPropertySetting[] {
    return state.settings;
  }

  @Selector()
  static isLoading(state: ActivityPropertySettingsStateModel): boolean {
    return state.isLoading;
  }

  /**
   * Returns the mobile availability map built from persisted settings once a
   * load attempt has completed, or `undefined` while the very first load is
   * still in progress — matching the `Signal<Map | undefined>` contract
   * expected by `hasLoadedAvailabilities` in `FormFieldsAvailabilityDirective`.
   *
   * When `isLoaded` is `true` but `settings` is empty (e.g. the device is
   * offline and nothing was cached), an empty `Map` is returned.  An empty map
   * means "no restrictions", so every field is treated as available and the
   * form renders immediately instead of blocking behind the loading skeleton.
   */
  @Selector()
  static mobileAvailability(
    state: ActivityPropertySettingsStateModel,
  ): AppActivityPropertiesAvailability | undefined {
    if (!state.isLoaded) {
      // eslint-disable-next-line no-undefined
      return undefined;
    }

    return new Map(
      state.settings.map((s) => [s.propertyName, s.mobileAvailability]),
    );
  }

  /**
   * Selector factory that returns the raw `PropertyAvailability` value for a
   * given ActivityModel property on mobile. Defaults to `'editable'` while the
   * settings are still loading or when the property has no explicit setting.
   */
  static propertyAvailability(
    propertyName: NestedKeyOf<ActivityModel>,
  ): (state: ActivityPropertySettingsStateModel) => PropertyAvailability {
    return createSelector(
      [ActivityPropertySettingsState],
      (state: ActivityPropertySettingsStateModel) => {
        if (!state.isLoaded) return 'editable';
        return (
          new Map(
            state.settings.map((s) => [s.propertyName, s.mobileAvailability]),
          ).get(propertyName) ?? 'editable'
        );
      },
    );
  }

  async ngxsAfterBootstrap(
    ctx: StateContext<ActivityPropertySettingsStateModel>,
  ): Promise<void> {
    const stored: ActivityPropertySetting[] | null =
      await this.storage.get(STATE_KEY);
    if (stored?.length) {
      ctx.patchState({ settings: stored, isLoaded: true });
    } else {
      // No cached settings — mark as loaded immediately so that offline users
      // are not stuck behind the loading skeleton waiting for a network timeout.
      // The gateway fetch initiated by InitializeActivityPropertySettingsState
      // will update settings (and isLoaded stays true) once connectivity returns.
      ctx.patchState({ isLoaded: true });
    }
  }

  @Action(InitializeActivityPropertySettingsState)
  initialize(
    ctx: StateContext<ActivityPropertySettingsStateModel>,
  ): Observable<ActivityPropertySetting[] | null> {
    ctx.patchState({ isLoading: true });

    return this.gateway.getSettings().pipe(
      tap((settings) => {
        ctx.patchState({ settings, isLoaded: true });
        this.storage.set(STATE_KEY, settings);
      }),
      catchError(() => {
        // Mark as loaded even on network failure so that offline users with no
        // cached settings are not stuck on the loading skeleton indefinitely.
        ctx.patchState({ isLoaded: true });
        return of(null);
      }),
      finalize(() => ctx.patchState({ isLoading: false })),
    );
  }
}
